import { FieldType, SourceType } from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { Operation } from 'components/organisms/tasks/filters/operations';
import { GroupSeparator } from 'components/organisms/tasks/filters/separators';
import { unreachable } from '../unreachable';
import { AssigneeFilter } from './fields/assignee';
import { BooleanFilter } from './fields/boolean';
import { DateFilter } from './fields/date';
import { EstimateFilter } from './fields/estimate';
import { MultiSelectFilter } from './fields/multi-select';
import { MultiUserFilter } from './fields/multi-user';
import { NumberFilter } from './fields/number';
import { SingleSelectFilter } from './fields/single-select';
import { SingleUserFilter } from './fields/single-user';
import { TextFilter } from './fields/text';
export const toApiFormat = (filter)=>{
    if (!filter.field) {
        return unreachable(filter);
    }
    switch(filter.field.type){
        case FieldType.Boolean:
            return BooleanFilter.toApiFormat(filter);
        case FieldType.Number:
            return NumberFilter.toApiFormat(filter);
        case FieldType.Date:
            return DateFilter.toApiFormat(filter);
        case FieldType.Estimate:
            return EstimateFilter.toApiFormat(filter);
        case FieldType.Text:
        case FieldType.MultiLineText:
        case FieldType.Note:
            return TextFilter.toApiFormat(filter);
        case FieldType.SingleSelect:
            return SingleSelectFilter.toApiFormat(filter);
        case FieldType.MultiSelect:
            return MultiSelectFilter.toApiFormat(filter);
        case FieldType.SingleUser:
            return SingleUserFilter.toApiFormat(filter);
        case FieldType.MultiUser:
            return MultiUserFilter.toApiFormat(filter);
        case FieldType.Assignee:
            return AssigneeFilter.toApiFormat(filter);
        default:
            return unreachable(filter.field);
    }
};
export const toSheetApiFormat = (filter)=>{
    if (!filter.field) {
        return unreachable(filter);
    }
    switch(filter.field.type){
        case FieldType.Boolean:
            return BooleanFilter.toApiFormat(filter);
        case FieldType.Number:
            return NumberFilter.toApiFormat(filter);
        case FieldType.Date:
            return DateFilter.toApiFormat(filter);
        case FieldType.Estimate:
            return EstimateFilter.toApiFormat(filter);
        case FieldType.Text:
        case FieldType.MultiLineText:
        case FieldType.Note:
            return TextFilter.toApiFormat(filter);
        case FieldType.SingleSelect:
            return SingleSelectFilter.toApiFormat(filter);
        case FieldType.MultiSelect:
            return MultiSelectFilter.toApiFormat(filter);
        case FieldType.SingleUser:
            return SingleUserFilter.toApiFormat(filter);
        case FieldType.MultiUser:
            return MultiUserFilter.toApiFormat(filter);
        case FieldType.Assignee:
            return AssigneeFilter.toApiFormat(filter);
        default:
            return toApiFormat(filter);
    }
};
const parse = (filter, field)=>{
    const fieldType = field.type;
    switch(fieldType){
        case FieldType.Boolean:
            return BooleanFilter.parse(filter, field);
        case FieldType.Number:
            return NumberFilter.parse(filter, field);
        case FieldType.Date:
            return DateFilter.parse(filter, field);
        case FieldType.Text:
        case FieldType.MultiLineText:
        case FieldType.Note:
            return TextFilter.parse(filter, field);
        case FieldType.SingleSelect:
            return SingleSelectFilter.parse(filter, field);
        case FieldType.MultiSelect:
            return MultiSelectFilter.parse(filter, field);
        case FieldType.SingleUser:
            return SingleUserFilter.parse(filter, field);
        case FieldType.MultiUser:
            return MultiUserFilter.parse(filter, field);
        case FieldType.Estimate:
            return EstimateFilter.parse(filter, field);
        case FieldType.Assignee:
            return AssigneeFilter.parse(filter, field);
        default:
            return unreachable(fieldType);
    }
};
const toNativeFieldFilterFormat = (filter)=>{
    if (!filter.field) {
        return unreachable(filter);
    }
    return {
        name: filter.field.fieldName,
        ...toApiFormat(filter)
    };
};
const toCustomFieldFilterFormat = (filter)=>{
    if (!filter.field) {
        return unreachable(filter);
    }
    return {
        fieldId: filter.field.fieldId,
        ...toApiFormat(filter)
    };
};
const toFieldFilterFromObject = (obj)=>{
    const validKeys = Object.keys(obj).filter((key)=>key.includes('*'));
    const fieldFilters = validKeys.map((key)=>{
        const [sourceType, restStr] = key.split('*');
        const queryDataArr = restStr.split('.');
        const operation = queryDataArr.pop();
        const name = queryDataArr.pop();
        const value = obj[key];
        return {
            sourceType: sourceType,
            operation,
            name,
            fieldId: queryDataArr.length > 0 ? Number(name) : undefined,
            value
        };
    });
    return fieldFilters;
};
const parseFromObject = (obj, fields)=>{
    const searchFields = toFieldFilterFromObject(obj);
    const filteredField = searchFields.map((currField)=>fields.find((fld)=>fld.fieldName === currField.name || fld.fieldId === currField.fieldId));
    return searchFields.map((currField, index)=>parse(currField, filteredField[index] || {}));
};
const getValueFromFilter = (filter, encode = true)=>{
    switch(filter.operation){
        case Operation.IsEmpty:
        case Operation.IsNotEmpty:
        case Operation.IsNull:
            return true;
        default:
            return filter.value ? encode ? encodeURIComponent(filter.value) : filter.value : undefined;
    }
};
const nativeFieldToQueryStringFormat = (field)=>`${field.key ? `${field.key}${GroupSeparator}` : ''}${field.sourceType}${GroupSeparator}${field.name}.${field.operation}=${getValueFromFilter(field)}`;
const nativeFieltdToQueryRecordFormat = (field)=>{
    const key = `${field.key ? `${field.key}${GroupSeparator}` : ''}${field.sourceType}${GroupSeparator}${field.name}.${field.operation}`;
    const value = getValueFromFilter(field, false);
    return [
        key,
        value
    ];
};
export const customFieldKeyToString = (field)=>`${field.key ? `${field.key}${GroupSeparator}` : ''}${field.sourceType}${GroupSeparator}field.${field.fieldId}`;
const customFieldToQueryStringFormat = (field)=>`${customFieldKeyToString(field)}.${field.operation}=${getValueFromFilter(field)}`;
const customFieldToQueryRecordFormat = (field)=>{
    const key = `${customFieldKeyToString(field)}.${field.operation}`;
    const value = getValueFromFilter(field);
    return [
        key,
        value
    ];
};
const toQueryStringFormat = (filters)=>[
        ...filters.filter((x)=>{
            var _x_field;
            return !((_x_field = x.field) === null || _x_field === void 0 ? void 0 : _x_field.userCreated);
        }).map(toNativeFieldFilterFormat).map(nativeFieldToQueryStringFormat),
        ...filters.filter((x)=>{
            var _x_field;
            return (_x_field = x.field) === null || _x_field === void 0 ? void 0 : _x_field.userCreated;
        }).map(toCustomFieldFilterFormat).map(customFieldToQueryStringFormat)
    ].join('&');
const convertQueryStringToObject = (filterString)=>{
    const filterObject = {};
    filterString.split('&').forEach((x)=>{
        const [key, value] = decodeURIComponent(x).split('=');
        if (Object.keys(filterObject).includes(key)) {
            if (!Array.isArray(filterObject[key])) {
                filterObject[key] = [
                    filterObject[key],
                    value
                ];
            } else {
                filterObject[key] = [
                    ...filterObject[key],
                    value
                ];
            }
        } else {
            filterObject[key] = value;
        }
    });
    return filterObject;
};
export const ApiFilter = {
    toNativeFieldFilterFormat,
    toCustomFieldFilterFormat,
    parse,
    parseFromObject,
    toQueryStringFormat,
    nativeFieldToQueryStringFormat,
    customFieldToQueryStringFormat,
    convertQueryStringToObject
};
const toNativeFieldFilterFormatForGroups = (filter)=>{
    const apiFormat = toApiFormat(filter.criteria);
    // Transform sourceType to snake_case for API / query-string compatibility (saved views, etc.)
    const sourceType = apiFormat.sourceType === SourceType.RevenueEntry ? 'revenue_entry' : apiFormat.sourceType === SourceType.ProjectFinancialBudget ? 'project_financials_budget' : apiFormat.sourceType === SourceType.ResourceAllocation ? 'resource_allocation' : apiFormat.sourceType;
    return {
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        name: filter.criteria.field.fieldName,
        ...filter.key ? {
            key: filter.key
        } : {},
        ...apiFormat,
        ...sourceType ? {
            sourceType: sourceType
        } : {}
    };
};
const toCustomFieldFilterFormatForGroups = (filter)=>{
    var _filter_criteria_field, _filter_criteria_field1;
    const apiFormat = toApiFormat(filter.criteria);
    // Transform sourceType to snake_case for API / query-string compatibility (saved views, etc.)
    const sourceType = apiFormat.sourceType === SourceType.RevenueEntry ? 'revenue_entry' : apiFormat.sourceType === SourceType.ProjectFinancialBudget ? 'project_financials_budget' : apiFormat.sourceType === SourceType.ResourceAllocation ? 'resource_allocation' : apiFormat.sourceType;
    return {
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fieldId: filter.criteria.field.fieldId,
        ...filter.key ? {
            key: filter.key
        } : {},
        ...apiFormat,
        ...sourceType ? {
            sourceType: sourceType
        } : {},
        ...((_filter_criteria_field = filter.criteria.field) === null || _filter_criteria_field === void 0 ? void 0 : _filter_criteria_field.fieldType) ? {
            fieldType: (_filter_criteria_field1 = filter.criteria.field) === null || _filter_criteria_field1 === void 0 ? void 0 : _filter_criteria_field1.fieldType
        } : {}
    };
};
const filterGroupCriteriaBySourceType = (filters, sourceType)=>filters.filter((x)=>FieldFilter.isValid(x.criteria) && x.criteria.field.sourceType === sourceType);
const filterGroupCriteriaBySourceTypes = (filters, sourceTypes)=>filters.filter((x)=>{
        if (!FieldFilter.isValid(x.criteria)) {
            return false;
        }
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check
        return sourceTypes.includes(x.criteria.field.sourceType);
    });
const toQueryStringFormatForGroups = (filters)=>[
        ...filters// @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        .filter((x)=>!x.criteria.field.userCreated).map(toNativeFieldFilterFormatForGroups).map(nativeFieldToQueryStringFormat),
        ...filters// @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        .filter((x)=>x.criteria.field.userCreated).map(toCustomFieldFilterFormatForGroups).map(customFieldToQueryStringFormat)
    ].join('&');
const toQueryStringFormatForGroupsForSourceType = (filters, sourceType)=>toQueryStringFormatForGroups(filterGroupCriteriaBySourceType(filters, sourceType));
const toQueryStringFormatForGroupsForSourceTypes = (filters, sourceTypes)=>toQueryStringFormatForGroups(filterGroupCriteriaBySourceTypes(filters, sourceTypes));
const toQueryRecordFormatForGroups = (filters)=>filters.reduce((acc, filter)=>{
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        if (!filter.criteria.field.userCreated) {
            // Native
            const currentFilter = toNativeFieldFilterFormatForGroups(filter);
            const [k, v] = nativeFieltdToQueryRecordFormat(currentFilter);
            return {
                ...acc,
                [k]: v
            };
        }
        // Custom
        const currentFilter = toCustomFieldFilterFormatForGroups(filter);
        const [k, v] = customFieldToQueryRecordFormat(currentFilter);
        return {
            ...acc,
            [k]: v
        };
    }, {});
export const ApiGroupFilter = {
    toNativeFieldFilterFormatForGroups,
    toCustomFieldFilterFormatForGroups,
    toQueryStringFormatForGroups,
    toQueryStringFormatForGroupsForSourceType,
    toQueryStringFormatForGroupsForSourceTypes,
    toQueryRecordFormatForGroups
};
// Re-exported so callers that reach this file through the lazy
// getSimpleSearchFilterModule() accessor (see utils/simple-search-filter/module.ts)
// can validate criteria without value-importing the tasks filter stack into
// their own (often entry) chunk. Already imported above, so this adds nothing
// to this chunk.
export { FieldFilter };
